import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, normalizeStyle as _normalizeStyle, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed } from "vue";
import {
  ArtifactCytoElement,
  ArtifactNodeProps,
  CytoCore,
  GraphElementType,
  GraphMode,
} from "@/types";
import { getEnumColor } from "@/util";
import {
  useTheme,
  deltaStore,
  selectionStore,
  subtreeStore,
  layoutStore,
  timStore,
} from "@/hooks";
import { CyElement } from "@/components/graph/base";
import ArtifactNodeActions from "./ArtifactNodeActions.vue";
import ArtifactNodeFooter from "./ArtifactNodeFooter.vue";
import ArtifactNodeDisplay from "./ArtifactNodeDisplay.vue";


/**
 * Renders an artifact node within the graph.
 */
const __default__ = {
  name: "ArtifactNode",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    artifactsInView: {},
    artifact: {}
  },
  setup(__props: any) {

const props = __props;

const { darkMode } = useTheme();

const id = computed(() => props.artifact.id);

const selected = computed(() => selectionStore.isArtifactInSelected(id.value));
const faded = computed(() => !props.artifactsInView.includes(id.value));
const hidden = computed(() =>
  subtreeStore.hiddenSubtreeNodes.includes(id.value)
);

const opacity = computed(() => (hidden.value ? 0 : faded.value ? 0.3 : 1));
const style = computed(
  () =>
    `opacity: ${opacity.value};` +
    (selected.value ? "z-index: 10;" : "z-index: 1;")
);

const hiddenChildren = computed(() => subtreeStore.getHiddenChildren(id.value));

const deltaState = computed(() => deltaStore.getArtifactDeltaType(id.value));
const showDelta = computed(() => deltaStore.inDeltaView);

const deltaColor = computed(() => getEnumColor(deltaState.value));
const typeColor = computed(() => timStore.getTypeColor(props.artifact.type));
const color = computed(() =>
  showDelta.value ? deltaColor.value : typeColor.value
);

const dataCy = computed(() =>
  selected.value ? "tree-node-selected" : "tree-node"
);

const definition = computed<ArtifactCytoElement>(() => {
  const { id, type, name } = props.artifact;

  return {
    data: {
      type: GraphElementType.node,
      graph: "tree" as GraphMode,
      id,

      artifactName: name,
      artifactType: type,

      dark: darkMode.value,
    },
  };
});

/**
 * Handles the add event to set this artifact's default position,
 * and highlight it if it is selected.
 */
function handleAdd(cy: CytoCore): void {
  cy.getElementById(id.value).layout(layoutStore.layoutOptions).run();

  if (id.value !== selectionStore.selectedArtifactId) return;

  selectionStore.selectArtifact(id.value);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(CyElement), {
    definition: definition.value,
    style: _normalizeStyle(style.value),
    "data-cy": dataCy.value,
    "data-cy-name": props.artifact.name,
    "data-cy-children": hiddenChildren.value.length,
    onAdd: handleAdd
  }, {
    default: _withCtx(() => [
      _createVNode(ArtifactNodeDisplay, {
        artifact: props.artifact,
        color: color.value,
        "delta-color": deltaColor.value,
        selected: selected.value
      }, null, 8, ["artifact", "color", "delta-color", "selected"]),
      _createVNode(ArtifactNodeFooter, {
        artifact: props.artifact,
        color: color.value,
        selected: selected.value,
        "hidden-children": hiddenChildren.value
      }, null, 8, ["artifact", "color", "selected", "hidden-children"]),
      _createVNode(ArtifactNodeActions, {
        artifact: props.artifact,
        color: color.value,
        selected: selected.value,
        "hidden-children": hiddenChildren.value
      }, null, 8, ["artifact", "color", "selected", "hidden-children"])
    ]),
    _: 1
  }, 8, ["definition", "style", "data-cy", "data-cy-name", "data-cy-children"]))
}
}

})